<template>
  <div>
    <v-container fluid class="pt-0 mt-2">
      <v-card class="pb-1 mb-0">
        <v-card-text class="pa-1">
          <v-row justify="center" align="center" v-if="loading">
            <div class="text-center">
              <div>
                <v-progress-circular
                  class="my-4"
                  :size="50"
                  width="5"
                  color="primary"
                  indeterminate
                ></v-progress-circular>
              </div>
              <p class="display-1">{{ $t("Loading") }}...</p>
            </div>
          </v-row>
          <v-row v-if="!loading" class="align-center pa-0">
            <v-col cols="4">
              <v-card-title class="pl-2">{{ title }}</v-card-title>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-bind:label="$t('Name')"
                v-model="accessRight.name"
                :error-messages="nameErrors"
                @input="$v.accessRight.name.$touch()"
                @blur="$v.accessRight.name.$touch()"
                dense
                class="ma-0 pa-0 pt-3"
              />
            </v-col>
            <v-col cols="4" class="text-right justify-space-between">
              <v-btn @click="close">
                {{ $t("Cancel") }}
              </v-btn>
              <v-btn
                color="primary"
                @click.prevent="save"
                :loading="saving"
              >
                {{ $t("Save") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-row dense class="mt-1 pt-0">
        <v-col cols="12" md="5">
          <v-card class="mb-2" height="100%">
            <v-card-subtitle>{{ $t("Documents") }}</v-card-subtitle>
            <v-card-text class="pb-0 pt-0">
              <v-row>
                <v-col>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Create") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch v-model="accessRight.objectCreate" />
                  </v-row>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Read") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch v-model="accessRight.objectRead" />
                  </v-row>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Edit") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch v-model="accessRight.objectUpdate" />
                  </v-row>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Delete") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch v-model="accessRight.objectDelete" />
                  </v-row>
                </v-col>
                <v-col>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Download") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch v-model="accessRight.objectDownload" />
                  </v-row>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Print") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch v-model="accessRight.objectPrint" />
                  </v-row>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Sign") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch v-model="accessRight.objectSign" />
                  </v-row>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("DistributeExternal") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch v-model="accessRight.objectExternalDistribution" />
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card class="mb-0" height="100%">
            <v-card-subtitle>{{ $t("DocumentAttachments") }}</v-card-subtitle>
            <v-card-text class="pb-0 pt-0">
              <v-row align="center" class="mx-2">
                <p>{{ $t("Create") }}</p>
                <v-spacer></v-spacer>
                <v-switch
                  color="green"
                  v-model="accessRight.documentAttachmentCreate"
                />
              </v-row>
              <v-row align="center" class="mx-2">
                <p>{{ $t("Read") }}</p>
                <v-spacer></v-spacer>
                <v-switch
                  color="green"
                  v-model="accessRight.documentAttachmentRead"
                />
              </v-row>
              <v-row align="center" class="mx-2">
                <p>{{ $t("Edit") }}</p>
                <v-spacer></v-spacer>
                <v-switch
                  color="green"
                  v-model="accessRight.documentAttachmentUpdate"
                />
              </v-row>
              <v-row align="center" class="mx-2">
                <p>{{ $t("Delete") }}</p>
                <v-spacer></v-spacer>
                <v-switch
                  color="green"
                  v-model="accessRight.documentAttachmentDelete"
                />
              </v-row>
              <v-row align="center" class="mx-2">
                <p>{{ $t("Download") }}</p>
                <v-spacer></v-spacer>
                <v-switch
                  color="green"
                  v-model="accessRight.documentAttachmentDownload"
                />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="mb-0">
            <v-card-subtitle>{{ $t("Comments") }}</v-card-subtitle>
            <v-card-text class="pb-0 pt-0">
              <v-row>
                <v-col>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Create") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch
                      color="orange"
                      v-model="accessRight.commentCreate"
                    />
                  </v-row>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Delete") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch
                      color="orange"
                      v-model="accessRight.commentDelete"
                    />
                  </v-row>
                </v-col>
                <v-col>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Edit") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch
                      color="orange"
                      v-model="accessRight.commentUpdate"
                    />
                  </v-row>
                  <v-row align="center" class="mx-2">
                    <p>{{ $t("Read") }}</p>
                    <v-spacer></v-spacer>
                    <v-switch color="orange" v-model="accessRight.commentRead" />
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card class="mb-2 mt-2">
            <v-card-subtitle>{{ $t("InternalTables") }}</v-card-subtitle>
            <v-card-text class="pb-0 pt-0">
              <v-row align="center" class="mx-2">
                <p>{{ $t("InsertData") }}</p>
                <v-spacer></v-spacer>
                <v-switch
                  color="purple"
                  v-model="accessRight.dynamicTablesAddData"
                />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <v-card class="mb-1">
            <v-card-text class="pb-0 pt-0">
              <v-row align="center" class="mx-2">
                <v-switch
                  color="primary"
                  v-model="selection"
                  @change="selectAll"
                  :label="$t('SelectAll')"
                />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>
<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

import AccessRightService from '@/services/AccessRightService';
import AccessRightsAdministration from '@/views/administration/contentType/rights/AccessRightsAdministration.vue';
import AccessRight from '@/ecm/models/AccessRight';
import Utils from '@/ecm/common/Utils';

export default {
    name: 'AccessRightConfig',
    components: {
        AccessRightsAdministration
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            vm.componentFrom = from;
        });
    },
    created() {
        this.$v.$reset();
        this.accessRightId = this.$route.params.id;
        if (this.accessRightId > 0) {
            this.loadAccessRight();
        } else {
            this.new();
        }
    },
    data() {
        return {
            title: this.$t('AccessRight'),
            selection: false,
            accessRightId: 0,
            accessRight: new AccessRight(),
            isNew: true,
            saving: false,
            loading: false,
            componentFrom: {}
        };
    },
    mixins: [validationMixin],
    validations: {
        accessRight: {
            name: { required },
        },
    },
    computed: {
        nameErrors() {
            const errors = [];
            if (!this.$v.accessRight.name.$dirty) return errors;
            !this.$v.accessRight.name.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
    },
    methods: {
        selectAll() {
            this.accessRight.objectCreate = this.selection;
            this.accessRight.objectRead = this.selection;
            this.accessRight.objectUpdate = this.selection;
            this.accessRight.objectDelete = this.selection;
            this.accessRight.objectDownload = this.selection;
            this.accessRight.objectPrint = this.selection;
            this.accessRight.objectSign = this.selection;
            this.accessRight.objectExternalDistribution = this.selection;
            this.accessRight.documentAttachmentCreate = this.selection;
            this.accessRight.documentAttachmentDelete = this.selection;
            this.accessRight.documentAttachmentRead = this.selection;
            this.accessRight.documentAttachmentUpdate = this.selection;
            this.accessRight.documentAttachmentDownload = this.selection;
            this.accessRight.commentCreate = this.selection;
            this.accessRight.commentRead = this.selection;
            this.accessRight.commentUpdate = this.selection;
            this.accessRight.commentDelete = this.selection;
            this.accessRight.dynamicTablesAddData = this.selection;
        },
        save() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.saving = true;
            AccessRightService.save(this.accessRight.toDto())
                .then((result) => {
                    if (this.isNew) {
                        Utils.toast.success(
                            this.$t('AccessRight') + ' ' + this.$t('CreatedSuccessfully')
                        );
                    } else {
                        Utils.toast.success(
                            this.$t('AccessRight') + ' ' + this.$t('UpdatedSuccessfully')
                        );
                    }
                    this.close();
                })
                .catch(() => {
                })
                .finally(() => {
                    //always
                    this.saving = false;
                });
        },
        loadAccessRight() {
            this.loading = true;
            AccessRightService.getById(this.accessRightId).then((result) => {
                this.accessRight = new AccessRight(result);
                this.isNew = false;
                this.selection = false;
            }).finally(() => {
                this.loading = false;
            });
        },
        new() {
            this.accessRight = new AccessRight();
            this.isNew = true;
            this.selection = false;
        },
        close() {
            if (this.componentFrom.name == AccessRightsAdministration.name) {
                this.$router.push(this.componentFrom.path);
            } else
                this.$router.push({ name: 'AccessRightList' });
        }
    },
};
</script>
